import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormikProps } from "formik"
import { isNil } from "lodash"
import { enqueueSnackbar } from "notistack"
import { useRef } from "react"
import { useNavigate } from "react-router-dom"

import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { TENANT_ADMIN_ROUTE, TENANT_USERS_ROUTE, getTenantUsersRoute } from "../../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { selectEffectiveCompanyId, selectEffectiveUserGroup } from "../../../../../context/contextSelectors"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useAppSelector } from "../../../../../store/storeHooks"
import { createUser } from "../../../../../users/userApi"
import { TenantAdminQuery } from "../../../tenantAdminApi"

import { TenantUserForm } from "../components/TenantUserForm"
import { TenantUserFormValues } from "../tenantUserTypes"
import { toCreateTenantUserBody } from "../utils/tenantUserBody"

export function CreateTenantUserPage() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userGroupId = useAppSelector(selectEffectiveUserGroup)

    const formRef = useRef<FormikProps<TenantUserFormValues>>(null)

    const { mutateAsync: callCreateUser, isLoading: isCreating } = useApiMutation(createUser, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TenantAdminQuery.USERS_FOR_TENANT] })

            navigate(getTenantUsersRoute("user"))
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    async function handleSubmit(values: TenantUserFormValues) {
        if (isNil(companyId) || isNil(userGroupId)) {
            enqueueSnackbar(l10n.getString("office-manager-create-user-missing-context-error"), { variant: "error" })
            return
        }

        await callCreateUser({ user: toCreateTenantUserBody(values, { companyId, userGroupId }) })
    }

    return (
        <>
            <LocalizedStrict id="office-manager-create-user-form-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.TENANT_CREATE_USER}
                    title="Create user"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    rightElement={
                        <LocalizedStrict id="office-manager-create-user-form">
                            <StandardLoadingButton type="submit" onClick={handleSaveClick} loading={isCreating}>
                                Create
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="office-manager-create-user-home-breadcrumb">
                            <BreadcrumbLink to={TENANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="tenant-nav-item-organization-header">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-create-user-users-breadcrumb">
                            <BreadcrumbLink to={TENANT_USERS_ROUTE}>Users</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-create-user-create-breadcrumb">
                            <Breadcrumb>Create user</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <TenantUserForm ref={formRef} isEditMode={false} onSubmit={handleSubmit} />
        </>
    )
}
